'use client'
import { LinkExternal } from '@/types/common/LinkExternal'
import s from './Header.module.scss'
import { useContext, useEffect, useState, useRef } from 'react'
import { usePathname, useRouter } from "next/navigation";
import { WebContext } from '@/context/webContext';
import { AnimatePresence, motion } from 'framer-motion'
import Link from 'next/link'
import React from 'react'
import Logo from '../Logo'
import { websiteSchema as websiteSchemaConst, createNavSchema, createWPHeaderSchema } from '@/lib/constants';

interface menuFooter {
  linksSocial: Array<LinkExternal>;
  links: Array<LinkExternal>;
  description: string | null;
  newsletterText: string | null;
  copyright: string | null;
}
interface FooterProps {
  footer: menuFooter;
}

interface menu {
  links: Array<LinkExternal>
}

interface menuList {
  menu: menu | null;
}

interface DataProps {
  menu: menuList | null;
  footer: FooterProps;
}

export default function Header({ menu, footer }: DataProps) {
  const { backgroundHeader, setBackgroundHeader, colorHeader, setColorHeader } = useContext(WebContext)
  const pathname = usePathname()
  const [hydrated, setHydrated] = useState(false);

  const isHome = pathname === "/";
  const [firstTimeInHome, setFirstTimeInHome] = useState(isHome);
  const [activeMenu, setActiveMenu] = useState<string | null>(null)
  const [searchOpen, setSearchOpen] = useState(false); // Estado para abrir/cerrar el formulario
  const searchInputRef = useRef<HTMLInputElement>(null); // Referencia al input
  const searchFormRef = useRef<HTMLFormElement>(null); // Referencia al formulario
  const router = useRouter();
  const [lastBackgroundHeader, setLastBackgroundHeader] = useState(true);
  const [lastColorHeader, setLastColorHeader] = useState('black');

  const [email, setEmail] = useState('');
  const [result, setResult] = useState(null);

  //SCHEMAS
  const navSchema = createNavSchema(menu?.menu?.links ?? []);
  const wpHeaderSchema = createWPHeaderSchema(menu?.menu?.links ?? []);
  const websiteSchema = websiteSchemaConst;

  useEffect(() => {
    if (isHome) {
    } else {
      setFirstTimeInHome(false);
    }
  }, [pathname]);

  useEffect(() => {
    if (!searchOpen || !activeMenu) {
      setBackgroundHeader(lastBackgroundHeader); // Restaura el valor original cuando el search se cierra
      setColorHeader(lastColorHeader);
    }
  }, [searchOpen, activeMenu]);

  // Efecto para enfocar el input cuando se abre el buscador
  useEffect(() => {
    if (searchOpen || activeMenu) {
      setLastBackgroundHeader(backgroundHeader); // Guarda el estado antes de abrir
      setLastColorHeader(colorHeader);
      setBackgroundHeader(false); // Lo pone en false cuando se abre el buscador
      setColorHeader('black');
      searchInputRef.current?.focus();
    }
  }, [searchOpen, activeMenu]);

  // Cerrar el formulario si se hace clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchFormRef.current &&
        !searchFormRef.current.contains(event.target as Node)
      ) {
        setSearchOpen(false);
      }
    };

    if (searchOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [searchOpen]);

  const handleSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const searchQuery = event.currentTarget.search.value
    router.push(`/search?query=${searchQuery}`)
    setActiveMenu(null)
    setSearchOpen(false)
  }

  useEffect(() => {
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (hydrated && pathname === "/") {
      setFirstTimeInHome(true);
    } else {
      setFirstTimeInHome(false);
    }
  }, [hydrated, pathname]);

  const showAnimation = hydrated && pathname === "/" && firstTimeInHome;

  const cleanPathname = pathname !== '/' && pathname.endsWith('/')
    ? pathname.slice(0, -1)
    : pathname;

  const subscribeUser = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    console.log(email);
    const res = await fetch('/api/subscribeUser', {
      body: JSON.stringify({
        email: { email }
      }),
      headers: {
        'Content-Type': 'application/json'
      },
      method: 'POST'
    })

    setResult(res);

    return res
  }

  if (!hydrated) return null;

  return (
    <>
      <header className={`${s.header} ${backgroundHeader ? s.backgroundHeader : s.transparent} ${colorHeader === 'black' ? s.black : s.white}`}>
        <motion.div
          key={showAnimation ? 'anim-home' : 'default'}
          initial={{ opacity: showAnimation ? 0 : 1 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, ease: 'easeOut', delay: showAnimation ? 1 : 0 }}
          className={s.logo}
          style={{ transformOrigin: "left center" }}
        >
          <Logo />
        </motion.div>

        <motion.div
          className={s.logoMobile}
          initial={{ opacity: firstTimeInHome ? 0 : 1 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
          viewport={{ once: true }}
          onClick={() => setActiveMenu(null)}
        >
          <Logo />
        </motion.div>

        <motion.div
          key={firstTimeInHome ? 'menu-anim' : 'menu-default'}
          className={s.menu}
          initial={{ opacity: firstTimeInHome ? 0 : 1 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, ease: 'easeOut', delay: firstTimeInHome ? 1 : 0 }}
        >
          <nav>
            <ul>
              {menu?.menu?.links.map((link, index) => {

                return (
                  <li key={`menu-link-${index}`} className={cleanPathname === link.url ? s.active : ''}>
                    <Link href={link.url} target={link.newWindow ? '_blank' : '_self'} onClick={() => setActiveMenu(null)}>
                      {link.title}
                    </Link>
                  </li>
                )
              })}
              <li
                key={`menu-link-last`}
                className={s.search}
                onClick={() => { setSearchOpen(true); setActiveMenu(null) }} // Solo abrir el buscador
                style={{ cursor: 'pointer' }}
              >
                SEARCH
              </li>
            </ul>
          </nav>
        </motion.div>

        <div className={s.burguerMenu} onClick={() => setActiveMenu(activeMenu === 'active' ? null : 'active')}>
          <span></span>
          <span></span>
          <span></span>
        </div>

        {hydrated && (
          <>
            <script type="application/ld+json" async dangerouslySetInnerHTML={{ __html: JSON.stringify(websiteSchema) }} />
            <script type="application/ld+json" async dangerouslySetInnerHTML={{ __html: JSON.stringify(navSchema) }} />
            <script type="application/ld+json" async dangerouslySetInnerHTML={{ __html: JSON.stringify(wpHeaderSchema) }} />
          </>
        )}

      </header>
      <AnimatePresence>
        {activeMenu && <motion.div
          className={`${s.headerMobile} ${activeMenu === 'active' ? s.active : ''}`}
          initial={{ opacity: 1, y: '-100%' }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 1, y: '-100%' }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
        >
          <div className={s.menu}>
            <nav>
              <ul>
                {menu?.menu?.links.map((link, index) => (
                  <li key={`menu-link-${index}`} className={cleanPathname === link.url ? s.active : ''}>
                    <Link href={link.url} target={link.newWindow ? '_blank' : '_self'} onClick={() => setActiveMenu(null)}>
                      {link.title}
                    </Link>
                  </li>
                ))}
                <li
                  key={`menu-link-last`}
                  className={s.search}
                  onClick={() => { setSearchOpen(true); setActiveMenu(null) }} // Solo abrir el buscador
                  style={{ cursor: 'pointer' }}
                >
                  SEARCH
                </li>
              </ul>
            </nav>
          </div>
          <div className={s.footerElements}>
            <div className={s.menus}>
              <nav className={s.social}>
                <ul>
                  {footer.footer.linksSocial.map((link, index) => (
                    <li key={`link-social-${index}`}>
                      <Link href={link.url} target={link.newWindow ? '_blank' : '_self'}>
                        {link.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </nav>
              <nav className={s.social}>
                <ul>
                  {footer.footer.links.map((link, index) => (
                    <li key={`link-politics-${index}`}>
                      <Link href={link.url} target={link.newWindow ? '_blank' : '_self'} onClick={() => setActiveMenu(null)}>
                        {link.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </nav>
            </div>
            <div className={s.newsletter}>
              <div className={s.newsletterText}>
                <p>NEWSLETTER</p>
                <p>
                  {footer.footer.newsletterText}
                </p>
              </div>

              <form onSubmit={subscribeUser}>
                <input type="email" placeholder="Your email" required onChange={(e) => setEmail(e.target.value)} value={email} />
                <button>JOIN</button>
              </form>
              <div className={s.answer}>{result && result.status == 200 ? <p>{"Thank you for subscribing"}</p> : result && result.status == 400 ? <p>{"You're already subscribed"}</p> : undefined}</div>
            </div>
            <div className={s.copyright}>
              <p>
                {footer.footer.copyright}
                <br />
                Code by <a href="https://www.ama.work" target="_blank" rel="noopener noreferrer">AMA</a>
              </p>
            </div>

          </div>
        </motion.div>}
      </AnimatePresence>
      {/* Search Form (Mostrado solo cuando searchOpen es true) */}
      {searchOpen && (
        <div className={`${s.searchForm}`}>
          <form onSubmit={handleSearch} ref={searchFormRef}>
            <input
              type="text"
              name="search"
              ref={searchInputRef} // Referencia al input
            />
            <button>Search</button>
          </form>
        </div>
      )}
    </>
  );
}